import { Button, CILabel, Dropdown, Input, Textarea } from 'ccs-ui-components';
import { SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { JourneyStepNameValidation } from './JourneyCanvasModals';

import { regex } from '../../constants/JdRegex';
import classes from './JourneyCanvasModals.module.scss';

interface Props {
  journeyStepName: string,
  setJourneyStepName: (arg: string) => void,
  invalidJourneyStepName: string,
  maxExecutionTimeInput: { hours: string, minutes: string, seconds: string },
  setMaxExecutionTimeInput: (arg: SetStateAction<{ hours: string, minutes: string, seconds: string }>) => void,
  uniqueIdentifiers: string[],
  setUniqueIdentifiers: (arg: string[]) => void,
  journeyStepDescription: string | undefined,
  setJourneyStepDescription: (arg: string) => void
  setInvalidJourneyStepName: (arg: string) => void,
  dropdownEndpointValues: {
    value: string;
    name: string;
  }[],
  endpointNodeId: string | undefined,
  setEndpointNodeId: (nodeId: string) => void,
  isEdit: boolean,
  readonly: boolean,
  cloning?: boolean,
}

/**
 * @description shows the modal after clicking save on canvas
 */
const JourneyStepModalBody: React.FC<Props> = ({ journeyStepName, setJourneyStepName, invalidJourneyStepName, setInvalidJourneyStepName, maxExecutionTimeInput, setMaxExecutionTimeInput, uniqueIdentifiers, setUniqueIdentifiers, journeyStepDescription, setJourneyStepDescription, dropdownEndpointValues, endpointNodeId, setEndpointNodeId, isEdit, readonly, cloning = false }: Props) => {
  const { t: translate } = useTranslation();

  const journeyStepMaxLength = 40;

  const [uniqueIdentifierTagsVal, setUniqueIdentifierTagsVal] = useState<string>('');
  const [isValidUniqueIdentifier, setIsValidUniqueIdentifier] = useState<boolean>(true);

  const journeyStepNameErrorText = () => {
    if (invalidJourneyStepName === JourneyStepNameValidation.EMPTY) {
      return translate('journey_builder_component.error_empty_journeyStepName');
    }
    if (invalidJourneyStepName === JourneyStepNameValidation.INVALID) {
      return translate('journey_builder_component.error_invalid_journeyStepName');
    }
    if (invalidJourneyStepName === JourneyStepNameValidation.LENGTH_EXCEEDED) {
      return translate('journey_builder_component.error_journeyStepName_length_exceeded');
    }
    return ('');
  };

  const journeyStepNameHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length === 0) {
      setInvalidJourneyStepName(JourneyStepNameValidation.EMPTY);
    } else if (e.target.value.length > journeyStepMaxLength) {
      setInvalidJourneyStepName(JourneyStepNameValidation.LENGTH_EXCEEDED);
    } else if (regex.name.test(e.target.value)) {
      setInvalidJourneyStepName(JourneyStepNameValidation.VALID);
    } else {
      setInvalidJourneyStepName(JourneyStepNameValidation.INVALID);
    }
    setJourneyStepName(e.target.value);
  };

  /**
   * @method maxExecTimeHandler
   * @description Set max execution time
   * @param state Journey canvas state
   * @param duration Time duration
   * @param field Execution time fields hr/min/sec
   * @returns Max execution time
   */
  const maxExecTimeHandler = (duration: string, field: string) => {
    let data = '';
    const time = Number(duration);

    /**
     * @method getValidTimeWithinMax
     * @description Function to get the valid time based on the maximum time allowed.
     * @param {number} time - The time value to check.
     * @param {number} maxTime - The maximum allowed time.
     * @returns {string} - The valid time as a string.
     */
    const getValidTimeWithinMax = (time: number, maxTime: number) => {
      if (Number(time) > maxTime) {
        return maxTime.toString();
      }
      if (Number(time) > 0) {
        return Number(time).toString();
      }
      return '0';
    };

    switch (field) {
      case 'Hours':
        data = getValidTimeWithinMax(time, 24);
        setMaxExecutionTimeInput({ ...maxExecutionTimeInput, hours: data });
        break;
      case 'Minutes':
        data = getValidTimeWithinMax(time, 59);
        setMaxExecutionTimeInput({ ...maxExecutionTimeInput, minutes: data });
        break;
      default:
        data = getValidTimeWithinMax(time, 59);
        setMaxExecutionTimeInput({ ...maxExecutionTimeInput, seconds: data });
        break;
    }
  };

  /**
  * @method hoursHandler
  * @description handles the hours on save modal
  * @param {React.ChangeEvent<HTMLInputElement>} e
  */
  const hoursHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    maxExecTimeHandler(e.target.value, 'Hours');
  };

  /**
  * @method minutesHandler
  * @description handles the minutes on save modal
  * @param {React.ChangeEvent<HTMLInputElement>} e
  */
  const minutesHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    maxExecTimeHandler(e.target.value, 'Minutes');
  };

  /**
  * @method secondsHandler
  * @description handles the seconds on save modal
  * @param {React.ChangeEvent<HTMLInputElement>} e
  */
  const secondsHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    maxExecTimeHandler(e.target.value, 'Seconds');
  };

  /**
  * @method clearTagsHandler
  * @description clear the tags
  */
  const clearTagsHandler = () => {
    setUniqueIdentifiers([]);
  };

  /**
  * @method handleTagsArea
  * @description handles the tags
  * @param tags
  */
  const handleTagsArea = (tags: string[]) => {
    const filteredTags = tags.filter((item, index) => ((tags.indexOf(item) === index) && /^[A-Za-z][A-Za-z0-9]*$/.test(item)));
    setUniqueIdentifiers(filteredTags);
  };

  const commitCurrentTag = (tag: string) => {
    const normalizedTag = tag.trim();
    if (!normalizedTag) {
      setUniqueIdentifierTagsVal('');
      setIsValidUniqueIdentifier(true);
      return;
    }
    handleTagsArea([...(uniqueIdentifiers ?? []), normalizedTag]);
    setUniqueIdentifierTagsVal('');
    setIsValidUniqueIdentifier(true);
  };

  const removeTag = (tag: string) => {
    const nextTags = (uniqueIdentifiers ?? []).filter((item) => item !== tag);
    setUniqueIdentifiers(nextTags);
  };

  const setUniqueIdentifiersValue = (val: string) => {
    if (regex.uniqueIdentifiers.test(val)) {
      setUniqueIdentifierTagsVal(val);
      setIsValidUniqueIdentifier(true);
    } else {
      setUniqueIdentifierTagsVal(val);
      setIsValidUniqueIdentifier(false);
    }
  };

  const uniqueIdentifierInputKeyHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      commitCurrentTag(uniqueIdentifierTagsVal);
    }
  };

  const uniqueIdentifierBlurHandler = () => {
    commitCurrentTag(uniqueIdentifierTagsVal);
  };

  const uniqueIdentifierPasteHandler = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedValue = event.clipboardData.getData('text');
    if (!pastedValue.includes(',') && !pastedValue.includes('\n')) {
      return;
    }
    event.preventDefault();
    const pastedTags = pastedValue
      .split(/[,\n]/)
      .map((item) => item.trim())
      .filter(Boolean);
    if (pastedTags.length === 0) {
      return;
    }
    handleTagsArea([...(uniqueIdentifiers ?? []), ...pastedTags]);
    setUniqueIdentifierTagsVal('');
    setIsValidUniqueIdentifier(true);
  };

  /**
  * @method UUIDDescriptionHandler
  * @description Used to update the state of journey description
  * @param {React.ChangeEvent<HTMLInputElement>} e
  */
  const stepDescriptionHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setJourneyStepDescription(e.target.value);
  };

  const endpointSelectHandler = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setEndpointNodeId(e.target.value);
  };

  return (
    <div className={`${classes.modal} ${readonly ? classes.readonly : ''}`} data-testid="stepModal">
      <div className={classes['modal-journey-name']}>
        <Input
          labelText={String(translate('journey_builder_component.txt_journey_step'))}
          isMandatory
          helperText={String(translate('journey_builder_component.txt_journey_helper'))}
          label={undefined}
          name=""
          placeholder={String(translate('journey_builder_component.txt_journey_place'))}
          value={journeyStepName}
          onChange={journeyStepNameHandler}
          error={invalidJourneyStepName !== JourneyStepNameValidation.VALID}
          errorTxt={journeyStepNameErrorText()}
          data-testid="modalJourneyStepName"
          disabled={isEdit || readonly}
        />
      </div>
      <div className={classes['modal-max-exec-time']}>
        <CILabel id="h" infoIconText={translate('journey_builder_component.max_exec_time_info_bubble')} labelText={translate('journey_builder_component.lbl_max_exec_time')} />
        <form className={classes['duration-form']} autoComplete="off">
          <input disabled={readonly} data-testid="hoursInput" id="h" name="h" type="text" value={maxExecutionTimeInput.hours} onChange={hoursHandler} maxLength={2} />
          <input disabled={readonly} data-testid="minutesInput" id="m" name="m" type="text" value={maxExecutionTimeInput.minutes} onChange={minutesHandler} maxLength={2} />
          <input disabled={readonly} data-testid="secondsInput" id="s" name="s" type="text" value={maxExecutionTimeInput.seconds} onChange={secondsHandler} maxLength={2} />
        </form>
        <p className={classes['duration-display']}>
          {maxExecutionTimeInput.hours || '0'} {translate('journey_builder_component.lbl_hours')}
          {maxExecutionTimeInput.minutes || '0'} {translate('journey_builder_component.lbl_minutes')}
          {maxExecutionTimeInput.seconds || '0'} {translate('journey_builder_component.lbl_seconds')}
        </p>
      </div>
      <div className={classes['modal-unique-identifiers']}>
        <span className={classes['modal-unique-identifiers-lbl']}>
          <CILabel id="tagsInput" labelText={translate('journey_builder_component.lbl_unique_identifiers')} infoIconText={translate('journey_builder_component.unique_identifier_info_bubble')} />
          <Button disabled={readonly} onClick={clearTagsHandler} data-testid="clearTags" className={classes['modal-unique-identifiers-clear-btn']}>{translate('journey_builder_component.btn_clear')}</Button>
        </span>
        <div className={classes.tags} data-testid="tagsInput">
          {(cloning ? [] : uniqueIdentifiers ?? []).map((tag) => (
            <span className={classes['tag-pill']} key={tag}>
              <span>{tag}</span>
              <button
                type="button"
                onClick={() => removeTag(tag)}
                disabled={readonly || cloning}
                className={classes['tag-remove-btn']}
                aria-label={`remove-${tag}`}
              >
                x
              </button>
            </span>
          ))}
          <input
            placeholder={String(translate('journey_builder_component.txt_add_a_tag'))}
            style={{ width: '40%' }}
            id="tagsInput"
            value={uniqueIdentifierTagsVal}
            onChange={(event) => setUniqueIdentifiersValue(event.target.value)}
            onKeyDown={uniqueIdentifierInputKeyHandler}
            onBlur={uniqueIdentifierBlurHandler}
            onPaste={uniqueIdentifierPasteHandler}
            disabled={readonly || cloning}
            className={classes['tag-input']}
          />
        </div>
        {
          (
            uniqueIdentifierTagsVal.length > 0 && !isValidUniqueIdentifier) && (
            <p className={classes['invalid-name']}>{translate('journey_builder_component.error_unique_identifier')}</p>
          )
        }
      </div>
      <div>
        <Textarea
          label=""
          labelText={String(translate('journey_builder_component.txt_description'))}
          name=""
          value={journeyStepDescription}
          onChange={stepDescriptionHandler}
          placeholder={String(translate('journey_builder_component.txt_desc_placeholder'))}
          data-testid="modalJourneyStepDesc"
          disabled={readonly}
        />
      </div>
      {
        dropdownEndpointValues.length !== 0 && (
          <>
            <div>
              <hr />
            </div>
            <div>
              <Dropdown
                label=""
                labelText={String(translate('journey_builder_component.txt_dropdown_label'))}
                isMandatory
                data={dropdownEndpointValues}
                onChange={endpointSelectHandler}
                value={endpointNodeId ?? ''}
                placeholder={String(translate('journey_builder_component.journeyStepName_dropdown_placeholder'))}
                data-testid="modalJourneyStepEndpoint"
                disabled={isEdit || readonly}
              />
            </div>
          </>
        )
      }
    </div>
  );
};

export default JourneyStepModalBody;
